import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { ClientTierInfo } from '../Models/ClientTierInfo';
import { PricingTierVersion } from '../Models/PricingTierVersion';
import { UserService } from '../services/user.service';

interface ClientTierRow {
  clientId: number;
  clientName: string;
  currentTierId: number | null;
  currentTierName: string | null;
  beneficiarySourceUrn: string | null;
  avgAppIncome: number | null;
  income: number | null;
  isMatchedInFile: boolean;
  suggestedTierId: number | null;
  suggestedTierName: string | null;
  tierMismatch: boolean;
  selectedTierId: number | null;
}

@Component({
  selector: 'app-admin-client-tier',
  templateUrl: './admin-client-tier.component.html',
  styleUrls: ['./admin-client-tier.component.css'],
  standalone: false
})
export class AdminClientTierComponent implements OnInit {

  allClients: ClientTierInfo[] = [];
  activeTiers: PricingTierVersion[] = [];
  matchedRows: ClientTierRow[] = [];
  unmatchedRows: ClientTierRow[] = [];

  fileUploaded = false;
  fileName = '';
  loading = false;
  saving = false;

  private charityIncomeMap = new Map<string, number>();
  private allRows: ClientTierRow[] = [];
  private loggedInUserTag = '';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private userService: UserService
  ) {}

  async ngOnInit() {
    const user = this.userService.loggedInUser;
    this.loggedInUserTag = `${user?.lastName}, ${user?.firstName}`;
    this.loading = true;
    try {
      const [clients, allVersions] = await Promise.all([
        lastValueFrom(this.http.get<ClientTierInfo[]>('/charitycentral/GetClientsForTierAlignment')),
        lastValueFrom(this.http.get<PricingTierVersion[]>('/charitycentral/GetAllPricingTierVersions'))
      ]);
      this.allClients = clients;
      this.activeTiers = allVersions
        .filter(v => v.endDate === null)
        .sort((a, b) => (a.legacyIncomeLowerBand ?? 0) - (b.legacyIncomeLowerBand ?? 0));
    } catch {
      this.toastr.error('Failed to load client data.', '', { timeOut: 4000 });
    } finally {
      this.loading = false;
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    this.fileName = file.name;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const map = this.parseFile(content);
      if (map.size === 0) return;
      this.charityIncomeMap = map;
      this.buildRows();
      this.fileUploaded = true;
    };
    reader.readAsText(file);
    // Reset input so the same file can be re-selected
    input.value = '';
  }

  private parseFile(content: string): Map<string, number> {
    const lines = content.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) {
      this.toastr.error('File appears to be empty or has no data rows.', '', { timeOut: 4000 });
      return new Map();
    }

    const headers = lines[0].split('\t').map(h => h.trim().toLowerCase());
    const crnIdx = headers.indexOf('registered_charity_number');
    const incomeIdx = headers.indexOf('income_legacies');

    if (crnIdx === -1 || incomeIdx === -1) {
      this.toastr.error(
        'File is missing required columns (registered_charity_number, income_legacies).',
        '', { timeOut: 5000 }
      );
      return new Map();
    }

    const grouped = new Map<string, number[]>();
    const maxIdx = Math.max(crnIdx, incomeIdx);
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split('\t');
      if (cols.length <= maxIdx) continue;
      const crn = cols[crnIdx].trim();
      if (!crn) continue;
      const incomeStr = (cols[incomeIdx] ?? '').trim().replace(/,/g, '');
      const income = parseFloat(incomeStr) || 0;
      if (!grouped.has(crn)) grouped.set(crn, []);
      grouped.get(crn)!.push(income);
    }

    const result = new Map<string, number>();
    grouped.forEach((values, crn) => {
      const nonZero = values.filter(v => v > 0);
      // Average of non-zero values; if all are zero the average is 0
      const avg = nonZero.length > 0
        ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length
        : 0;
      result.set(crn, avg);
    });
    return result;
  }

  private buildRows() {
    this.allRows = this.allClients.map(client => {
      const urn = client.beneficiarySourceUrn?.trim() ?? null;
      const isMatchedInFile = !!urn && this.charityIncomeMap.has(urn);
      const income = isMatchedInFile ? this.charityIncomeMap.get(urn!)! : null;
      const suggestedTier = income !== null ? this.getSuggestedTier(income) : null;
      const tierMismatch = !!suggestedTier && suggestedTier.name !== client.pricingTierName;

      return {
        clientId: client.id,
        clientName: client.name,
        currentTierId: client.pricingTierId,
        currentTierName: client.pricingTierName,
        beneficiarySourceUrn: urn,
        avgAppIncome: client.avgAppIncome,
        income,
        isMatchedInFile,
        suggestedTierId: suggestedTier?.id ?? null,
        suggestedTierName: suggestedTier?.name ?? null,
        tierMismatch,
        selectedTierId: tierMismatch ? (suggestedTier?.id ?? null) : null
      };
    });

    this.matchedRows = this.allRows
      .filter(r => r.isMatchedInFile)
      .sort((a, b) => (b.tierMismatch ? 1 : 0) - (a.tierMismatch ? 1 : 0)
        || a.clientName.localeCompare(b.clientName));

    this.unmatchedRows = this.allRows
      .filter(r => !r.isMatchedInFile)
      .sort((a, b) => a.clientName.localeCompare(b.clientName));
  }

  private getSuggestedTier(income: number): PricingTierVersion | null {
    for (const tier of this.activeTiers) {
      const lower = tier.legacyIncomeLowerBand ?? 0;
      const upper = tier.legacyIncomeUpperBand;
      if (income >= lower && (upper === null || income <= upper)) {
        return tier;
      }
    }
    return null;
  }

  get mismatchCount(): number {
    return this.matchedRows.filter(r => r.tierMismatch).length;
  }

  get changedCount(): number {
    return this.allRows.filter(r => r.selectedTierId !== null).length;
  }

  applyAllSuggested() {
    this.matchedRows
      .filter(r => r.tierMismatch)
      .forEach(r => { r.selectedTierId = r.suggestedTierId; });
  }

  clearAll() {
    this.allRows.forEach(r => { r.selectedTierId = null; });
  }

  async saveChanges() {
    const updates = this.allRows
      .filter(r => r.selectedTierId !== null)
      .map(r => ({ clientId: r.clientId, newPricingTierId: r.selectedTierId! }));

    if (!updates.length) {
      this.toastr.info('No tier changes selected.', '', { timeOut: 3000 });
      return;
    }

    this.saving = true;
    try {
      await lastValueFrom(
        this.http.post('/charitycentral/BulkUpdateClientPricingTiers', updates, {
          params: { user: this.loggedInUserTag }
        })
      );
      this.toastr.success(`${updates.length} client tier(s) updated successfully.`, '', { timeOut: 3000 });
      this.allClients = await lastValueFrom(
        this.http.get<ClientTierInfo[]>('/charitycentral/GetClientsForTierAlignment')
      );
      this.buildRows();
    } catch {
      this.toastr.error('Failed to save tier changes.', '', { timeOut: 4000 });
    } finally {
      this.saving = false;
    }
  }
}
